import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@swyftx/aviary/atoms/Accordion/Accordion';
import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { SelectItemData } from '@swyftx/aviary/atoms/Select';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';
import { Funnel } from '@swyftx/aviary/icons/outlined';
import { ClearTableFilters } from '@swyftx/aviary/molecules/TableFilters';
import { TableSelectFilter } from '@swyftx/aviary/molecules/TableFilters/TableSelectFilter';

import { MarginInstrument } from '@routes/SwyftxMargin/components/General';
import { TransactionsFilterDate } from '@routes/Transactions/components/TransactionsFilters/TransactionsFilterDate';

import { DateTime } from 'luxon';
import { useMarginInstruments } from 'src/lib/margin/hooks/instruments';
import { MarginHistoryDirection } from 'src/lib/margin/types';
import { useMarkets } from 'src/lib/markets/hooks/useMarkets';

import { useMarginPositionsTableFilterStore } from '../../useMarginPositionsTableFilterStore';
import { marginHistoryDirections, marginHistoryEvents } from '../MarginHistoryTable/MarginHistoryTable.data';

type Props = {
  show?: boolean;
  startDate?: DateTime;
  endDate?: DateTime;
  directions?: MarginHistoryDirection[];
  events?: string[];
  instrument?: string;
  setStartDate?: (startDate?: DateTime) => void;
  setEndDate?: (endDate?: DateTime) => void;
  setDirections?: (directions: MarginHistoryDirection[]) => void;
  setEvents?: (events: string[]) => void;
  setInstrument?: (instrument?: string) => void;
};

const MarginPositionsTableFilters: React.FC<Props> = ({
  show,
  startDate,
  endDate,
  directions,
  events,
  instrument,
  setStartDate,
  setEndDate,
  setDirections,
  setEvents,
  setInstrument,
}) => {
  const isSmallScreen = !useTailwindBreakpoint('md', true);
  const { t } = useTranslation('margin');
  const { markets } = useMarginInstruments();
  const { getAssetByUuid } = useMarkets();
  const { resetFilters, filterStartDate, filterEndDate, filterDirections, filterEvents, filterCurrentInstrument } =
    useMarginPositionsTableFilterStore();

  const instruments = useMemo((): SelectItemData<string>[] => {
    if (!markets) return [];

    return markets.map((market): SelectItemData<string> => {
      const secondary = getAssetByUuid(market.secondaryAssetUuid);
      const primary = getAssetByUuid(market.primaryAssetUuid);

      return {
        leadingIcon: <MarginInstrument market={market} showMarketName={false} showMarketSymbol={false} size='medium' />,
        label: `${secondary?.code}${primary?.code}`,
        secondaryLabel: `${secondary?.name} / ${primary?.name}`,
        value: market.uuid,
      };
    });
  }, [getAssetByUuid, markets]);

  const canReset = useMemo(() => {
    if (filterStartDate || filterEndDate) return true;
    if (filterDirections && filterDirections.length > 0) return true;
    if (filterEvents && filterEvents.length > 0) return true;
    if (filterCurrentInstrument && filterCurrentInstrument.length > 0) return true;

    return false;
  }, [filterCurrentInstrument, filterDirections, filterEndDate, filterEvents, filterStartDate]);

  const totalFiltersActive = useMemo(() => {
    let totalFilters = 0;

    if (filterStartDate || filterEndDate) totalFilters++;
    if (filterDirections?.length) totalFilters++;
    if (filterEvents?.length) totalFilters++;
    if (filterCurrentInstrument?.length) totalFilters++;

    return totalFilters;
  }, [filterCurrentInstrument, filterDirections?.length, filterEndDate, filterEvents.length, filterStartDate]);

  if (isSmallScreen) {
    return (
      <Accordion type='single' className='w-full' collapsible>
        <AccordionItem value='filters' className='rounded-0 border-none'>
          <AccordionTrigger className='rounded-[0%] border-color-border-main data-[state=open]:border-b'>
            <FlexLayout direction='row' alignItems='center' className='w-full' spacing={8}>
              <Funnel className='h-20 w-20 text-color-text-primary' alt='filter' />
              <Body weight='bold'>
                {t('tables.positions.filters.filters', {
                  totalFilters: totalFiltersActive > 0 ? `(${totalFiltersActive})` : '',
                })}
              </Body>
            </FlexLayout>
          </AccordionTrigger>
          <AccordionContent className='mx-0 p-16 pb-16'>
            <FlexLayout direction='column' className='w-full ' alignItems='center' justifyContent='center' spacing={16}>
              {setStartDate && setEndDate && (
                <TransactionsFilterDate
                  className='w-full'
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  isSmallScreen={isSmallScreen}
                />
              )}
              {setInstrument && (
                <TableSelectFilter<string>
                  className='w-full'
                  title={t('tables.positions.filters.instrument')}
                  items={instruments}
                  value={instrument ? [instrument] : []}
                  multi={false}
                  showSearch
                  onChange={(values) => setInstrument(values[0])}
                  isSmallScreen={isSmallScreen}
                />
              )}
              {setDirections && (
                <TableSelectFilter<MarginHistoryDirection>
                  className='w-full'
                  title={t('tables.positions.filters.direction')}
                  items={marginHistoryDirections}
                  value={directions}
                  onChange={(values) => setDirections(values)}
                  isSmallScreen={isSmallScreen}
                />
              )}
              {setEvents && (
                <TableSelectFilter<string>
                  title={t('tables.positions.filters.actionEvent')}
                  className='w-full'
                  items={marginHistoryEvents(t)}
                  multi
                  showSearch
                  value={events}
                  onChange={(values) => setEvents(values)}
                  isSmallScreen={isSmallScreen}
                />
              )}
              <Button
                variant='ghost'
                color='subtle'
                className='w-full'
                size='md'
                onClick={resetFilters}
                disabled={!canReset}
              >
                {t('tables.positions.filters.resetFilters')}
              </Button>
            </FlexLayout>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }

  if (!show) return null;

  return (
    <FlexLayout alignItems='center' spacing={8} className='border-b border-color-border-main p-8'>
      {setStartDate && setEndDate && (
        <TransactionsFilterDate
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isSmallScreen={false}
        />
      )}
      {setInstrument && (
        <TableSelectFilter<string>
          title={t('tables.positions.filters.instrument')}
          items={instruments}
          value={instrument ? [instrument] : []}
          multi={false}
          showSearch
          onChange={(values) => setInstrument(values[0])}
          isSmallScreen={isSmallScreen}
        />
      )}
      {setDirections && (
        <TableSelectFilter<MarginHistoryDirection>
          title={t('tables.positions.filters.direction')}
          items={marginHistoryDirections}
          value={directions}
          onChange={(values) => setDirections(values)}
          isSmallScreen={isSmallScreen}
        />
      )}
      {setEvents && (
        <TableSelectFilter<string>
          title={t('tables.positions.filters.actionEvent')}
          items={marginHistoryEvents(t)}
          multi
          value={events}
          showSearch
          onChange={(values) => setEvents(values)}
          isSmallScreen={isSmallScreen}
        />
      )}
      <ClearTableFilters onClick={resetFilters} disabled={!canReset} />
    </FlexLayout>
  );
};

export { MarginPositionsTableFilters };
